﻿import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import { sendMatterportLinks } from './sendMatterportLinks';
import { Translate } from '../../../../shared/LanguageService';
import { useTelemetryContext } from '../../../../telemetry/telemetryContext';
import Tooltip from '@mui/material/Tooltip';
import SuccessSnackbar, { SuccessSnackbarProps } from '../../../../shared/feedback/SuccessSnackbar';
import { AuthenticatedUser } from '../../../../shared/types';
import { getUserInfo } from '../../../../telemetry/utils';
import FailureSnackbar from '../../../../shared/feedback/FailureSnackbar';

type SendMatterportLinkFormProps = {
    matterportUrl: string;
    orderId: number;
    brokerAgentEmail: string;
    user: AuthenticatedUser;
};
type SendMatterportLinkSuccessSnackBarProps = {} & SuccessSnackbarProps;

function SendMatterportLinkForm(props: SendMatterportLinkFormProps) {
    let [showSendMatterportLinkSnackBarSuccess, setShowSendMatterportLinkSnackBarSuccess] =
        useState<boolean>(false);
    let [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    let [isError, setIsError] = useState<boolean>(false);
    
    const { logger } = useTelemetryContext();

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsSubmitting(true);
        const formData = new FormData(event.target as HTMLFormElement);
        const url = formData.get('url') as string;
        const orderId = formData.get('orderId') as string;
        const brokerAgentEmail = formData.get('brokerAgentEmail') as string;

        try {
            await sendMatterportLinks(Number(orderId), brokerAgentEmail, url);
            setShowSendMatterportLinkSnackBarSuccess(true);
        } catch (error) {
            let additionalInfo = {
                origin: 'SendMatterportLinkForm.handleSubmit',
                user: getUserInfo(props.user)
            };
            logger.logException(error, additionalInfo);
            setIsError(true);
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleCloseSendMatterportLinkSnackBarSuccess() {
        setShowSendMatterportLinkSnackBarSuccess(false);
    }

    function SendMatterportLinkSuccessSnackBar({
        open,
        onClose,
        text
    }: SendMatterportLinkSuccessSnackBarProps) {
        return <SuccessSnackbar open={open} onClose={onClose} text={text} />;
    }

    return (
        <form onSubmit={handleSubmit}>
            <input type="hidden" name="url" value={props.matterportUrl} />
            <input type="hidden" name="orderId" value={props.orderId.toString()} />
            <input type="hidden" name="brokerAgentEmail" value={props.brokerAgentEmail} />
            <Box>
                <Tooltip
                    title={Translate('view-order/basic-order-info/matterport/send-link-label')}
                >
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                        <i className="material-icons md-18">send</i>
                        {Translate('view-order/basic-order-info/matterport/send-link-button')}
                    </Button>
                </Tooltip>
            </Box>
            <SendMatterportLinkSuccessSnackBar
                open={showSendMatterportLinkSnackBarSuccess}
                onClose={handleCloseSendMatterportLinkSnackBarSuccess}
                text={Translate('view-order/basic-order-info/matterport/success-snackbar')}
            />
            <FailureSnackbar
                onClose={() => {
                    setIsError(false);
                }}
                open={isError}
                text={Translate('view-order/basic-order-info/matterport/failure-snackbar')}
            />
        </form>
    );
}

export default SendMatterportLinkForm;
